type Rect = { bottom: number; left: number; right: number; top: number };
type Placement = 'above' | 'below' | 'left' | 'right';

const MARGIN = 16;
const GAP = 12;
const MIN_FOCUS_HEIGHT = 56;
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

export function getGuidedTourLayout({
  bounds,
  cardHeight,
  cardWidth,
  preferredPlacement,
  target,
  viewportHeight,
}: {
  bounds: { left: number; right: number };
  cardHeight: number;
  cardWidth: number;
  preferredPlacement?: 'above';
  target: Rect;
  viewportHeight: number;
}) {
  const width = Math.min(cardWidth, bounds.right - bounds.left);
  const height = Math.min(cardHeight, viewportHeight - MARGIN * 2);
  const highlight = {
    left: Math.max(bounds.left, target.left - 6),
    right: Math.min(bounds.right, target.right + 6),
    top: clamp(target.top - 6, MARGIN, viewportHeight - MARGIN),
    bottom: clamp(target.bottom + 6, MARGIN, viewportHeight - MARGIN),
  };
  const alignRight = highlight.left + highlight.right > bounds.left + bounds.right;
  const alignedLeft = clamp(
    alignRight ? highlight.right - width : highlight.left,
    bounds.left,
    bounds.right - width
  );
  const alignedTop = clamp(highlight.top, MARGIN, viewportHeight - MARGIN - height);
  let placement: Placement;
  let left = alignedLeft;
  let top: number;

  if (preferredPlacement === 'above') {
    placement = 'above';
    highlight.top = Math.min(
      highlight.bottom - Math.min(MIN_FOCUS_HEIGHT, highlight.bottom - highlight.top),
      Math.max(highlight.top, MARGIN + height + GAP)
    );
    top = Math.max(MARGIN, highlight.top - GAP - height);
  } else if (highlight.bottom + GAP + height <= viewportHeight - MARGIN) {
    placement = 'below';
    top = highlight.bottom + GAP;
  } else if (highlight.top - GAP - height >= MARGIN) {
    placement = 'above';
    top = highlight.top - GAP - height;
  } else if (highlight.right + GAP + width <= bounds.right) {
    placement = 'right';
    left = highlight.right + GAP;
    top = alignedTop;
  } else if (highlight.left - GAP - width >= bounds.left) {
    placement = 'left';
    left = highlight.left - GAP - width;
    top = alignedTop;
  } else {
    // Large maps/charts can fill the viewport. Highlight their visible portion
    // and reserve an adjacent strip for the dialog instead of covering the data.
    const belowBottom = viewportHeight - MARGIN - height - GAP;
    const aboveTop = MARGIN + height + GAP;
    if (belowBottom - highlight.top >= MIN_FOCUS_HEIGHT) {
      placement = 'below';
      highlight.bottom = Math.min(highlight.bottom, belowBottom);
      top = highlight.bottom + GAP;
    } else if (highlight.bottom - aboveTop >= MIN_FOCUS_HEIGHT) {
      placement = 'above';
      highlight.top = Math.max(highlight.top, aboveTop);
      top = highlight.top - GAP - height;
    } else {
      const focusedBottom = Math.min(highlight.bottom, highlight.top + MIN_FOCUS_HEIGHT);
      const focusedTop = Math.max(highlight.top, highlight.bottom - MIN_FOCUS_HEIGHT);
      const spaceBelow = viewportHeight - MARGIN - focusedBottom - GAP;
      const spaceAbove = focusedTop - GAP - MARGIN;
      if (spaceBelow >= spaceAbove) {
        placement = 'below';
        highlight.bottom = focusedBottom;
        top = highlight.bottom + GAP;
      } else {
        placement = 'above';
        highlight.top = focusedTop;
        top = MARGIN;
      }
    }
  }

  const maxHeight =
    placement === 'above' ? highlight.top - GAP - MARGIN : viewportHeight - MARGIN - top;
  const arrow =
    placement === 'above' || placement === 'below'
      ? {
          left: clamp((highlight.left + highlight.right) / 2 - left, 24, width - 24),
          top: placement === 'below' ? 0 : Math.min(height, maxHeight),
        }
      : {
          left: placement === 'right' ? 0 : width,
          top: clamp(
            (highlight.top + highlight.bottom) / 2 - top,
            24,
            Math.min(height, maxHeight) - 24
          ),
        };

  return {
    arrow,
    card: { left, maxHeight, top, width },
    highlight: {
      height: Math.max(0, highlight.bottom - highlight.top),
      left: highlight.left,
      top: highlight.top,
      width: Math.max(0, highlight.right - highlight.left),
    },
    placement,
  };
}
